import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  LockClosedIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function buildUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.detail || data?.error || `Request failed (${response.status})`);
  return data;
}

const TERMINAL_STATUSES = new Set([
  'succeeded',
  'partial',
  'failed',
  'review_required',
]);

export default function FinancialManagementFilingPanel({ todo = null, caseId = '', caseNumber = '', onFiled }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [certificateId, setCertificateId] = useState('');
  const [debtorIds, setDebtorIds] = useState(() => new Set());
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);
  const [redactionAcknowledged, setRedactionAcknowledged] = useState(false);
  const [filing, setFiling] = useState(null);
  const [error, setError] = useState('');
  const [acceptingCertificateId, setAcceptingCertificateId] = useState('');
  const notifiedFilingId = useRef('');
  const sourcePath = todo
    ? `/firm/todays-todos/${todo.id}/financial-management-filing`
    : `/firm/cases/${caseId}/financial-management-filing`;

  const applyPreflight = (data, preferredCertificateId = '') => {
    setPreflight(data);
    const preferred = (data.certificates || []).find(
      certificate => String(certificate.id) === String(preferredCertificateId)
        && certificate.eligible
    );
    const eligible = preferred || (data.certificates || []).find(
      certificate => certificate.eligible
    );
    setCertificateId(eligible ? String(eligible.id) : '');
    const availableDebtors = (data.debtors || []).filter((debtor) => !debtor.already_filed);
    const defaults = availableDebtors.length === 1 ? availableDebtors : [];
    setDebtorIds(new Set(defaults.map((debtor) => Number(debtor.id))));
    const latest = data.latest_filing;
    setFiling(
      latest && ['queued', 'in_progress', 'review_required'].includes(latest.status)
        ? latest
        : null
    );
    setWarningAcknowledged(false);
    setRedactionAcknowledged(false);
  };

  const loadPreflight = async (preferredCertificateId = '') => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`${sourcePath}/preflight/`);
      applyPreflight(data, preferredCertificateId);
      return data;
    } catch (requestError) {
      setError(requestError.message || 'Could not check this certificate.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setOpen(true);
    loadPreflight();
  };

  useEffect(() => {
    if (!open || !filing?.id || !['queued', 'in_progress'].includes(filing.status)) return undefined;
    let cancelled = false;
    let polling = false;
    const poll = async () => {
      if (cancelled || polling) return;
      polling = true;
      try {
        const current = await apiFetch(`/firm/financial-management-filings/${filing.id}/`);
        if (cancelled) return;
        setFiling(current);
        if (
          current.status === 'succeeded'
          && notifiedFilingId.current !== current.id
        ) {
          notifiedFilingId.current = current.id;
          onFiled?.(todo || undefined);
        }
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Could not check filing status.');
      } finally {
        polling = false;
      }
    };
    poll();
    const timer = window.setInterval(poll, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [filing?.id, filing?.status, onFiled, open, todo]);

  const chooseCertificate = (certificate) => {
    setCertificateId(String(certificate.id));
    const available = (preflight?.debtors || []).filter((debtor) => !debtor.already_filed);
    setDebtorIds(new Set(
      (available.length === 1 ? available : []).map((debtor) => Number(debtor.id))
    ));
    setWarningAcknowledged(false);
    setRedactionAcknowledged(false);
  };

  const toggleDebtor = (debtorId) => {
    setDebtorIds((current) => {
      const next = new Set(current);
      if (next.has(debtorId)) next.delete(debtorId);
      else next.add(debtorId);
      return next;
    });
  };

  const viewCertificate = async (certificate) => {
    const viewer = window.open('', '_blank');
    if (viewer) viewer.opener = null;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(buildUrl(certificate.download_url), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Document could not be opened (${response.status}).`);
      const objectUrl = window.URL.createObjectURL(await response.blob());
      if (viewer) viewer.location = objectUrl;
      else window.open(objectUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    } catch (requestError) {
      if (viewer) viewer.close();
      setError(requestError.message || 'Could not open the certificate.');
    }
  };

  const acceptCertificate = async (certificate) => {
    const certificateKey = String(certificate.id);
    setAcceptingCertificateId(certificateKey);
    setError('');
    let acceptanceError = '';
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(buildUrl(`/firm/financial-management-certificates/${certificate.id}/accept/`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        acceptanceError = payload.detail || payload.error || `Accept failed (${response.status}).`;
      }

      // Read the saved status again so both the ToDo and Case Home entry points
      // immediately enable and select the same newly accepted certificate.
      const refreshed = await loadPreflight(certificateKey);
      const accepted = (refreshed?.certificates || []).find(
        item => String(item.id) === certificateKey
      );
      if (!accepted?.eligible) {
        setError(
          acceptanceError
          || accepted?.inspection?.message
          || 'The certificate could not be accepted for filing.'
        );
      }
    } catch (requestError) {
      await loadPreflight(certificateKey);
      setError(requestError.message || 'The certificate could not be accepted.');
    } finally {
      setAcceptingCertificateId('');
    }
  };

  const startFiling = async () => {
    setLoading(true);
    setError('');
    try {
      const current = await apiFetch(
        `${sourcePath}/start/`,
        {
          method: 'POST',
          body: JSON.stringify({
            certificate_id: Number(certificateId),
            debtor_ids: Array.from(debtorIds),
            warning_acknowledged: warningAcknowledged,
            redaction_acknowledged: redactionAcknowledged,
          }),
        }
      );
      setFiling(current);
      if (current.status === 'succeeded' && notifiedFilingId.current !== current.id) {
        notifiedFilingId.current = current.id;
        onFiled?.(todo || undefined);
      }
    } catch (requestError) {
      setError(requestError.message || 'The filing could not be started.');
    } finally {
      setLoading(false);
    }
  };

  const resolveUncertainFiling = async (outcome) => {
    const prompt = outcome === 'filed'
      ? 'Confirm only after you checked CM/ECF and found the filing on the docket. Mark it filed?'
      : 'Confirm only after you checked CM/ECF and verified that no filing was created. Allow a new attempt?';
    if (!window.confirm(prompt)) return;
    setLoading(true);
    setError('');
    try {
      const current = await apiFetch(
        `/firm/financial-management-filings/${filing.id}/resolve/`,
        { method: 'POST', body: JSON.stringify({ outcome }) }
      );
      setFiling(current);
      if (outcome === 'filed') onFiled?.(todo || undefined);
      else await loadPreflight();
    } catch (requestError) {
      setError(requestError.message || 'The docket review could not be saved.');
    } finally {
      setLoading(false);
    }
  };

  const cancelQueuedFiling = async () => {
    if (!window.confirm(
      'Cancel this stalled queue request? This does not file anything or mark the certificate complete. Restart the Celery worker before trying again.'
    )) return;
    setLoading(true);
    setError('');
    try {
      await apiFetch(
        `/firm/financial-management-filings/${filing.id}/cancel-queued/`,
        { method: 'POST' }
      );
      await loadPreflight();
    } catch (requestError) {
      setError(requestError.message || 'The stalled request could not be cancelled.');
    } finally {
      setLoading(false);
    }
  };

  const selectedCertificate = (preflight?.certificates || []).find(
    (certificate) => String(certificate.id) === certificateId
  );
  const filingRunning = filing && ['queued', 'in_progress'].includes(filing.status);
  const canFile = Boolean(
    preflight?.case_ready
    && preflight?.credentials?.ready_to_file
    && selectedCertificate?.eligible
    && debtorIds.size > 0
    && warningAcknowledged
    && redactionAcknowledged
    && !filingRunning
  );

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
      >
        <LockClosedIcon className="h-5 w-5" />
        File automatically with CM/ECF
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="financial-management-filing-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !filingRunning) setOpen(false);
          }}
        >
          <div className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
              <div>
                <h2 id="financial-management-filing-title" className="text-xl font-semibold text-slate-900">
                  File Certificate of Debtor Education
                </h2>
                <p className="text-sm text-slate-500">Case {preflight?.case_number || todo?.case?.case_number || caseNumber || '—'}</p>
              </div>
              <button
                type="button"
                onClick={() => !filingRunning && setOpen(false)}
                disabled={filingRunning}
                className="rounded-full p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
                aria-label="Close filing window"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-5 p-5">
              <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-red-950">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="mt-0.5 h-7 w-7 shrink-0 text-red-700" />
                  <div>
                    <h3 className="font-bold">Verify the document before filing</h3>
                    <p className="mt-1 text-sm leading-6">
                      The selected PDF must say <strong>Certificate of Debtor Education</strong>.
                      Do not file the pre-filing <strong>Credit Counseling Certificate</strong> with this event.
                    </p>
                  </div>
                </div>
              </div>

              {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}
              {loading && !preflight && <div className="py-8 text-center text-slate-600">Checking the certificate and filing setup…</div>}

              {preflight && !filingRunning && !filing && (
                <>
                  {!preflight.case_ready && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{preflight.case_message}</div>
                  )}
                  {!preflight.credentials?.ready_to_file && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                      CM/ECF automatic filing is not ready. Finish the CM/ECF Automatic Filing section on Account Management first.
                    </div>
                  )}

                  <section>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="font-semibold text-slate-900">1. Choose and open the certificate</h3>
                      <button
                        type="button"
                        onClick={() => loadPreflight(certificateId)}
                        disabled={loading || !!acceptingCertificateId}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh certificate status
                      </button>
                    </div>
                    <div className="mt-3 space-y-3">
                      {(preflight.certificates || []).map((certificate) => (
                        <label key={certificate.id} className={`block rounded-lg border p-4 ${certificate.eligible ? 'border-slate-200' : 'border-amber-200 bg-amber-50'}`}>
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-start gap-3">
                              <input
                                type="radio"
                                name={`financial-management-certificate-${todo?.id || caseId}`}
                                value={certificate.id}
                                checked={certificateId === String(certificate.id)}
                                onChange={() => chooseCertificate(certificate)}
                                disabled={!certificate.eligible}
                                className="mt-1 h-4 w-4 border-slate-300 text-indigo-600"
                              />
                              <div>
                                <div className="font-medium text-slate-900">{certificate.filename}</div>
                                <div className={`mt-1 text-sm ${certificate.eligible ? 'text-green-700' : 'text-amber-800'}`}>
                                  {certificate.status !== 'accepted'
                                    ? 'Awaiting review. Open the PDF, then accept it here to enable filing.'
                                    : certificate.inspection.message}
                                </div>
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  viewCertificate(certificate);
                                }}
                                className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                              >
                                <EyeIcon className="h-4 w-4" /> Open PDF
                              </button>
                              {certificate.status === 'awaiting_review' && (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    acceptCertificate(certificate);
                                  }}
                                  disabled={acceptingCertificateId === String(certificate.id) || !certificate.inspection?.valid}
                                  className="inline-flex items-center gap-2 rounded-md bg-green-700 px-3 py-2 text-sm font-semibold text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {acceptingCertificateId === String(certificate.id) ? (
                                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <CheckCircleIcon className="h-4 w-4" />
                                  )}
                                  {acceptingCertificateId === String(certificate.id)
                                    ? 'Accepting…'
                                    : 'Accept certificate'}
                                </button>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                      {(preflight.certificates || []).length === 0 && (
                        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                          No Financial Management certificate is uploaded for this case.
                        </p>
                      )}
                    </div>
                  </section>

                  <section>
                    <h3 className="font-semibold text-slate-900">2. Select each debtor named on the PDF</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      When both debtors are selected, CM/ECF will select both names together and file one joint certificate event.
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {(preflight.debtors || []).map((debtor) => (
                        <label key={debtor.id} className={`flex items-start gap-3 rounded-lg border p-4 ${debtor.already_filed ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}>
                          <input
                            type="checkbox"
                            checked={debtor.already_filed || debtorIds.has(Number(debtor.id))}
                            onChange={() => toggleDebtor(Number(debtor.id))}
                            disabled={debtor.already_filed}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600"
                          />
                          <span>
                            <span className="block font-medium text-slate-900">{debtor.label}: {debtor.name}</span>
                            {debtor.already_filed && <span className="mt-1 block text-xs font-semibold text-green-700">Already filed</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </section>

                  <section className="space-y-3">
                    <h3 className="font-semibold text-slate-900">3. Final filing confirmations</h3>
                    <label className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                      <input
                        type="checkbox"
                        checked={warningAcknowledged}
                        onChange={(event) => setWarningAcknowledged(event.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-red-300 text-red-600"
                      />
                      <span className="text-sm font-medium leading-6 text-red-950">
                        I opened this PDF and verified it is a <strong>Certificate of Debtor Education</strong>, not a pre-filing Credit Counseling certificate.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-4">
                      <input
                        type="checkbox"
                        checked={redactionAcknowledged}
                        onChange={(event) => setRedactionAcknowledged(event.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600"
                      />
                      <span className="text-sm leading-6 text-slate-700">
                        I reviewed the PDF and confirm it complies with the court&apos;s redaction rules.
                      </span>
                    </label>
                  </section>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={startFiling}
                      disabled={!canFile || loading}
                      className="inline-flex items-center gap-2 rounded-md bg-red-700 px-5 py-3 text-sm font-bold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <LockClosedIcon className="h-5 w-5" />
                      {loading ? 'Starting…' : 'File Certificate of Debtor Education'}
                    </button>
                  </div>
                </>
              )}

              {filing && (
                <section className={`rounded-xl border p-5 ${
                  filing.status === 'succeeded'
                    ? 'border-green-200 bg-green-50'
                    : filing.status === 'review_required'
                      ? 'border-red-300 bg-red-50'
                      : filing.status === 'failed' || filing.status === 'partial'
                        ? 'border-amber-300 bg-amber-50'
                        : 'border-indigo-200 bg-indigo-50'
                }`}>
                  <div className="flex items-start gap-3">
                    {filing.status === 'succeeded' ? (
                      <CheckCircleIcon className="h-7 w-7 shrink-0 text-green-700" />
                    ) : filingRunning ? (
                      <ArrowPathIcon className="h-7 w-7 shrink-0 animate-spin text-indigo-700" />
                    ) : (
                      <ExclamationTriangleIcon className="h-7 w-7 shrink-0 text-amber-700" />
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-slate-900">{filing.status_display}</h3>
                      <p className="mt-1 text-sm leading-6 text-slate-700">{filing.status_message}</p>
                      {filing.status === 'queued' && filing.queue_delay_seconds >= 20 && (
                        <p className="mt-2 text-sm font-semibold text-amber-800">
                          The Celery worker has not claimed this request yet. It may be stopped or running an older deployment.
                        </p>
                      )}
                      {filing.error_message && <p className="mt-2 text-sm font-medium text-red-800">{filing.error_message}</p>}
                      {(filing.results || []).length > 0 && (
                        <ul className="mt-3 space-y-2 text-sm text-slate-700">
                          {filing.results.map((result) => (
                            <li key={result.debtor_id} className="rounded-md bg-white/80 px-3 py-2">
                              <strong>{result.debtor_label}</strong>: {result.status === 'succeeded' ? 'Filed' : result.status}
                              {result.docket_number ? ` — docket ${result.docket_number}` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {filing.status === 'review_required' && (
                    <div className="mt-4 rounded-md border border-red-300 bg-white p-4">
                      <p className="text-sm font-semibold text-red-900">
                        Do not retry until you check the live CM/ECF docket; the final submit may have succeeded.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" onClick={() => resolveUncertainFiling('filed')} disabled={loading} className="rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800 disabled:opacity-60">
                          I checked — it filed
                        </button>
                        <button type="button" onClick={() => resolveUncertainFiling('not_filed')} disabled={loading} className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60">
                          I checked — it did not file
                        </button>
                      </div>
                    </div>
                  )}

                  {filing.status === 'queued' && filing.can_cancel_queued && (
                    <div className="mt-4 rounded-md border border-amber-300 bg-white p-4">
                      <p className="text-sm text-amber-900">
                        Restarting the worker may allow this request to continue. If the worker discarded the task as unregistered, cancel this request before trying again.
                      </p>
                      <button
                        type="button"
                        onClick={cancelQueuedFiling}
                        disabled={loading}
                        className="mt-3 rounded-md border border-amber-400 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-60"
                      >
                        {loading ? 'Cancelling…' : 'Cancel stalled queue request'}
                      </button>
                    </div>
                  )}

                  {TERMINAL_STATUSES.has(filing.status) && filing.status !== 'review_required' && filing.status !== 'succeeded' && (
                    <button
                      type="button"
                      onClick={async () => {
                        setFiling(null);
                        await loadPreflight();
                      }}
                      disabled={loading}
                      className="mt-4 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-60"
                    >
                      <ArrowPathIcon className="h-4 w-4" /> Review before another attempt
                    </button>
                  )}
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
